import { useCallback, useState } from 'react';
import { useCart, useSettings, useRegion, useDeliveryDate, useMeals, useSelection } from '@root/MyChoice/context';
import { useToasts } from '@root/lib/components/Toasts';
import { usePrompts } from '@root/lib/components/Prompt';
import ItemUnAvailableError from '@root/lib/errors/ItemUnavailableError';
import * as CartService from '@root/lib/services/CartService';
import * as SubscriptionsService from '@root/lib/services/SubscriptionsService';
import * as StorefrontApiService from '@root/lib/services/StorefrontApiService';
import * as TrackingService from '@root/lib/services/TrackingService';
import DiscountCodeNotFoundError from '@root/lib/errors/DiscountCodeNotFoundError';
import TagIcon from '@root/lib/components/Icons/Tag';
import Attention from '@root/lib/components/Icons/Attention';
import Tag from '@root/lib/components/Tag';
import useLoading from '@root/lib/hooks/useLoading';
import subscriptionSession from '@root/lib/classes/SubscriptionSession';
import dayjs from '@root/lib/packages/dayjs';
import MenuWeek from '@root/lib/classes/MenuWeek';
import cn from 'classnames';
import './Summary.scss';

/**
 * Creates and adds a hidden input firld to the form provided
 *
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} value
 */
const addHiddenInput = (form, name, value) => {
  const input = document.createElement('input');

  input.type = 'hidden';
  input.name = name;
  input.value = value;

  form.appendChild(input);
}

/**
 * Creates a checkout form and passes it to the provided function to add inputs before
 * submitting it so that Cashier can hijack it
 *
 * @param {(form: HTMLFormElement) => void} fn
 */
const createAndSubmitCheckoutForm = (fn) => {
  const form = document.createElement('form');
  form.action = '/checkout';
  form.method = 'POST';
  form.style.display = 'none';
  fn(form);

  // Adding submit button to form
  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.style.display = 'none';
  submitButton.addEventListener('click', BOLD.checkout.pushToCashier);

  form.appendChild(submitButton);
  document.body.appendChild(form);
  submitButton.click();
}

/**
 * Ensures that a shipping discount code is applicable or not by adding/readding the code to a checkout
 * instance and returning the state of the `applicable` field for that code.
 * 
 * Shopify currently has a bug with shipping discount codes specifically where if you add the code to a checkout
 * instance before there are any items in the instance, the code will not applicable and will continue to show not applicable
 * when items are added to the instance. But if you add the items and then the code, the discount will be applicable. This function
 * readds the discount code to the order right before we check the applicable state of the field just to verify that it is TRUELY false
 * 
 * @param {string} checkoutId 
 * @param {string} shippingDiscountCode 
 */
const ensureShippingDiscount = async (checkoutId, shippingDiscountCode) => {
  const resp = await StorefrontApiService.setCheckoutDiscountCode(checkoutId, shippingDiscountCode, [
    'discounts: discountApplications (first: 20)', [
      'edges', [
        'discount: node', [
          '... on DiscountCodeApplication', [
            'applicable',
            'code',
          ],
        ],
      ]
    ],
  ]);

  return resp.discounts.edges.find(d => d.discount.code === shippingDiscountCode)?.discount?.applicable ?? false;
}

const Summary = ({
  evaluatedShipping,
}) => {
  const [ cart, cartApi ] = useCart();
  const appliedDiscount = cartApi.getDiscountCode();

  const [ meals, mealsApi ] = useMeals();
  const [ region ] = useRegion();
  const [ ,setSelection ] = useSelection();
  const [ deliveryDate ] = useDeliveryDate();
  const [ discountCode, setDiscountCode ] = useState(appliedDiscount?.code ?? '');
  const [ discountCodeError, setDiscountCodeError ] = useState('');
  const [ settings, settingsApi ] = useSettings();
  const [ checkingOut, wrapCheckingOut ] = useLoading(false);
  const [ verifyingDiscount, setVerifyingDiscount ] = useState(false);
  const [, { addToast }] = useToasts();
  const [, { prompt }] = usePrompts();

  const timeSlot = mealsApi.getTimeSlot();
  const isCheckoutDisabled = cartApi.isLoading()
    || verifyingDiscount
    || checkingOut
    || settingsApi.isLoading()
    || (cart.itemCount ?? 0) === 0
    || (cart.itemCount ?? 0) < settings.minMeals
    || (cart.itemCount ?? 0) > settings.maxMeals
    || settings.incompatSubscription;
  const shouldHideDiscount = settings.isReturn && settings.inProcessingWindow;

  /**
   * Handles the click to remove the discount code. Checks that something
   * is not already happening with the discount code before trying to remove
   * it
   */
  const handleDiscountCodeRemoval = useCallback(() => {
    if (cartApi.isLoading('discountCode')) return;
    cartApi.removeDiscountCode();
  }, [cartApi.isLoading, cartApi.removeDiscountCode]);

  /**
   * Handles the change event of the discount code input and sets the discount code
   * state to the value of the input. Also unsets any discount code error
   *
   * @type {(e: React.ChangeEvent<HTMLInputElement>) => void}
   */
  const handleDiscountCodeChange = useCallback((e) => {
    setDiscountCodeError('');
    setDiscountCode(e.target.value);
  }, [setDiscountCode]);

  /**
   * Handles the submittion of the discount code form and adds the discount code to the
   * cart
   *
   * @type {(e: React.FormEvent<HTMLFormElement>) => void}
   */
  const handleDiscountSubmit = useCallback((e) => {
    e.preventDefault();
    cartApi.setDiscountCode(discountCode).then(() => {
      setDiscountCode('');
    }).catch(e => {
      if (e instanceof DiscountCodeNotFoundError) {
        setDiscountCodeError('Enter a valid discount code');
        return;
      }

      console.error(e);
    });
  }, [cartApi.setDiscountCode, discountCode]);
  /**
   * Adds items to the global cart and checks the user out
   */
  const checkout = useCallback(async () => {
    await wrapCheckingOut(false, async () => {
      const menuWeek = MenuWeek.fromDate(deliveryDate);
      const deliveryDay = dayjs(deliveryDate).format('ddd').toUpperCase();
      const subInfo = SubscriptionsService.getSubscriptionInformationForProductId(Shopify.product.id);
      const billingRule = subInfo.billing_rules[0];
      const intervalId = billingRule.id;
      const intervalText = billingRule.interval_name;
      const subGroupId = billingRule.subscription_group_id;
      const affiliateId = sessionStorage.getItem('affiliate_id');
      let items = cart.lineItems.map(li => ({
        quantity: li.quantity,
        id: li.variant.id,
      }));
      items = [
        {
          id: Shopify.product.variants[0].id,
          quantity: 1,
        },
        ...items,
      ];

      try {
        await CartService.clear();
        await CartService.addAttributes({
          delivery_day: deliveryDay,
          menu_id: menuWeek.menuId,
          affiliate_id: affiliateId,
        });
        const [ container, ...cartMeals ] = await CartService.addItems(...items);
        const variants = meals.flatMap(meal => meal.variants);
        TrackingService.trackPreselections({
          initial: true,
          amount: cart.total / 100,
          amount_cents: cart.total,
          week: menuWeek.menuId,
          time_slot_id: timeSlot.id,
          delivery_date: dayjs(deliveryDate).format('YYYY-MM-DDTHH:mm:ssZ'),
          meals_array: cart.lineItems.map(item => {
            const variant = variants.find(variant => variant.id === item.variant.id);
            return {
              sku: variant.sku,
              image: variant.images.smallUrl,
              price: item.unitPrice / 100,
              price_cents: item.unitPrice,
              quantity: item.quantity,
              name: item.title,
            };
          }),
        });

        // Giving tracking services enough time to submit event before submitting the form
        await new Promise(r => setTimeout(r, 1000));

        createAndSubmitCheckoutForm(form => {
          if (appliedDiscount?.code) addHiddenInput(form, 'discount', appliedDiscount.code);
          addHiddenInput(form, 'bold_cart_params[pre][order_data]', `{"customer":{"shipping":{"postal_code":"${region.zipcode}","country_code":"US"}}}`);

          addHiddenInput(form, 'bold_cart_params[bold_subscriptions][line_items_subscription_info][0][line_item_id]', container.key);
          addHiddenInput(form, 'bold_cart_params[bold_subscriptions][line_items_subscription_info][0][interval_id]', intervalId);
          addHiddenInput(form, 'bold_cart_params[bold_subscriptions][line_items_subscription_info][0][interval_text]', intervalText);
          addHiddenInput(form, 'bold_cart_params[bold_subscriptions][line_items_subscription_info][0][subscription_group_id]', subGroupId);

          addHiddenInput(form, 'bold_cart_params[bold_subscription_builder][line_items_info][0][line_item_id]', container.key);
          addHiddenInput(form, 'bold_cart_params[bold_subscription_builder][line_items_info][0][box_id]', timeSlot.box_id);
          addHiddenInput(form, 'bold_cart_params[bold_subscription_builder][line_items_info][0][time_slot_id]', timeSlot.id);

          for (let i = 0; i < cartMeals.length; i++) {
            const meal = cartMeals[i];
            const boxProduct = timeSlot.box_products.find(bp => parseInt(bp.platform_variant_id) === meal.variant_id);
            const namespace = `bold_cart_params[bold_subscription_builder][line_items_info][0][choices][${i}]`;

            addHiddenInput(form, `${namespace}[line_item_id]`, meal.key);
            addHiddenInput(form, `${namespace}[box_product_id]`, boxProduct.id);
            addHiddenInput(form, `${namespace}[choice_quantity]`, meal.quantity);
          }
        });
      } catch (e) {
        if (e instanceof ItemUnAvailableError) {
          addToast(<span><strong>Not enough stock to fulfill:</strong> {e.itemTitle}</span>, true);
        }

        console.error(e);
      }
    });
  }, [
    cart,
    deliveryDate,
    timeSlot,
    meals,
    appliedDiscount?.code,
    region?.zipcode,
    addToast,
  ]);

  /**
   * Takes the items in the cart instance and created
   */
  const submitSelections = useCallback(async () => {
    let unapplicableDiscountCode = appliedDiscount?.applicable === false;

    // Re-adding the discount if not applicable to ensure that is truely not applicable or if
    // Shopify is just being buggy
    if (unapplicableDiscountCode) {
      try {
        setVerifyingDiscount(true);
        unapplicableDiscountCode = !(await ensureShippingDiscount(cart.id, appliedDiscount.code));
      } catch (e) {
        console.error(e);
        setVerifyingDiscount(false);
        return;
      }
    }

    // Alerting the customer that their discount code is not applicable and if they wish to proceed it will
    // be removed
    if (unapplicableDiscountCode) {
      // Language variables
      const returnFlowLangauge = Shopify.language.my_choice.return_flow;
      const content = returnFlowLangauge.inapplicable_discount;
      const cancel = returnFlowLangauge.inapplicable_discount_cancel;
      const proceed = returnFlowLangauge.inapplicable_discount_proceed;

      let [ response ] = prompt({content, options: [
        {content: cancel, id: 'cancel', secondary: true},
        {content: proceed, id: 'proceed'},
      ]});
      response = await response.catch(() => 'cancel');
      if (response === 'cancel') return;
    }

    await wrapCheckingOut(false, async () => {
      const subscriptionLineItemId = settings.subscription.line_items[0].bold_platform_subscription_line_item_id;
      const subscriptionId = settings.subscription.bold_platform_subscription_id;

      // Because discounts can cause multiple line items to have the same variant id, we first
      // need to gather all the box products with the same id togehter before submitting them to
      // SuBu or SuBu will error as it does not expect multiple line items to have the same ID
      /** @type {Map<number, number>} */
      const boxProductQuantities = new Map();
      for (const item of cart.lineItems) {
        const variantId = item.variant.id;
        const boxProduct = timeSlot.box_products.find(bp => parseInt(bp.platform_variant_id) === variantId);
        const currentQuantity = boxProductQuantities.get(boxProduct.id) ?? 0;
        boxProductQuantities.set(boxProduct.id, currentQuantity + item.quantity);
      }

      let selection = {
        time_slot_id: timeSlot.id,
        bold_platform_subscription_line_item_id: subscriptionLineItemId,
        order_datetime: settings.futureOrderDate,
        line_items: Array.from(boxProductQuantities.entries()).map(([ id, quantity ]) => {
          return {
            box_product_id: id,
            choice_quantity: quantity,
          }
        }),
      };

      // Not doing anything with discount codes if in processing window
      if (!shouldHideDiscount) {
        
        // Setting discount code if code currently set on subscription does not match the one
        // set locally
        const currentDiscountCode = settings.subscription.discount_code;
        const newDiscountCode = appliedDiscount?.code ?? '';
        if (currentDiscountCode !== newDiscountCode && appliedDiscount?.applicable) {
          await SubscriptionsService.subscriptions.partiallyUpdate(subscriptionSession, subscriptionId, {discount_code: newDiscountCode});
        } else if (!appliedDiscount?.applicable && currentDiscountCode !== '') {
          await SubscriptionsService.subscriptions.partiallyUpdate(subscriptionSession, subscriptionId, {discount_code: ''});
        }
      }

      selection = await SubscriptionsService.createSelection(subscriptionSession, selection);
      setSelection(selection);
      const variants = meals.flatMap(meal => meal.variants);
      TrackingService.trackPreselections({
        initial: false,
        amount: cart.total / 100,
        amount_cents: cart.total,
        week: MenuWeek.fromDate(deliveryDate).menuId,
        time_slot_id: timeSlot.id,
        delivery_date: dayjs(deliveryDate).format('YYYY-MM-DDTHH:mm:ssZ'),
        subscription_id: subscriptionId,
        meals_array: cart.lineItems.map(item => {
          const variant = variants.find(variant => variant.id === item.variant.id);
          return {
            sku: variant.sku,
            image: variant.images.smallUrl,
            price: item.unitPrice / 100,
            price_cents: item.unitPrice,
            quantity: item.quantity,
            name: item.title,
          };
        }),
      });
    });
  }, [
    appliedDiscount?.code,
    timeSlot,
    cart,
    meals,
    settings.futureOrderDate,
    settings.subscription,
    deliveryDate,
    prompt,
    shouldHideDiscount,
  ]);

  return (
    <div className="SidecartSummary">
      <div className="SidecartSummary__Totals">
        <div className="SidecartSummary__TotalLabel">Subtotal</div>
        <div className="SidecartSummary__TotalValue">{Shopify.formatMoney(cart?.subtotal ?? 0)}</div>
        <div className="SidecartSummary__TotalLabel" style={{display: shouldHideDiscount ? 'none' : undefined}}>
          Discount
          {appliedDiscount?.targetType === 'LINE_ITEM' && (
            <div className="SidecartSummary__DiscountCode">
              <TagIcon className="tag" />
              {appliedDiscount.code}
            </div>
          )}
        </div>
        <div className="SidecartSummary__TotalValue" style={{display: shouldHideDiscount ? 'none' : undefined}}>{Shopify.formatMoney(cart?.totalDiscount ?? 0)}</div>
        <div className="SidecartSummary__TotalLabel">Shipping</div>
        <div className="SidecartSummary__TotalValue">
          {evaluatedShipping.loading ? (
            'Calculating...'
          ): evaluatedShipping.originalPrice === 0 ? (
            <span className="SidecartSummary__ShippingPrice">{Shopify.formatMoney(evaluatedShipping.originalPrice ?? 0)}</span>
          ) : (
            <>
              {evaluatedShipping.price === 0 && (
                <span className="SidecartSummary__FreeShipping">FREE</span>
              )}
              <span className="SidecartSummary__ShippingPrice">{Shopify.formatMoney(evaluatedShipping.originalPrice ?? 0)}</span>
            </>
          )}
        </div>
        <div className="SidecartSummary__TotalLabel">Estimated Sales Tax</div>
        <div className="SidecartSummary__TotalValue">{Shopify.formatMoney(cart?.totalTax ?? 0)}</div>
      </div>
      <div className="SidecartSummary__Totals">
        <div className="SidecartSummary__TotalLabel SidecartSummary__TotalLabel--bold">Order Total</div>
        <div className="SidecartSummary__TotalValue SidecartSummary__TotalValue--bold">
          {evaluatedShipping.loading ? (
            'Calculating...'
          ) : (
            Shopify.formatMoney(cart?.total + (evaluatedShipping?.price ?? 0))
          )}
        </div>
      </div>
      <hr />
      <form className="SidecartSummary__Discount" onSubmit={handleDiscountSubmit} style={{display: shouldHideDiscount ? 'none' : undefined}}>
        <div className="SidecartSummary__DiscountInputWrapper">
          <label
            children="Discount Code"
            className={cn({
              'SidecartSummary__DiscountLabel': true,
              'SidecartSummary__DiscountLabel--error': discountCodeError !== '',
            })}
          />
          <input
            className={cn({
              'SidecartSummary__DiscountInput': true,
              'SidecartSummary__DiscountInput--error': discountCodeError !== '',
            })}
            disabled={cartApi.isLoading('discountCode')}
            type="text"
            onChange={handleDiscountCodeChange}
            value={discountCode}
          />
        </div>
        <button
          className="SidecartSummary__DiscountSubmit"
          disabled={discountCode === '' || cartApi.isLoading('discountCode')}
          type="submit"
          children="Apply"
        />
        {discountCodeError !== '' && (
          <span className="SidecartSummary__DiscountError">{discountCodeError}</span>
        )}
        {appliedDiscount && (
          <div className="SidecartSummary__DiscountTags">
            <Tag onRemove={handleDiscountCodeRemoval} disabled={cartApi.isLoading('discountCode')}>
              {appliedDiscount.code}
            </Tag>
          </div>
        )}
      </form>
      <button className="SidecartSummary__Checkout" disabled={isCheckoutDisabled} onClick={settings.isReturn ? submitSelections : checkout}>
        {settings.incompatSubscription ? (
          'Disabled'
        ): settingsApi.isLoading() ? (
          'Loading...'
        ): (cart.itemCount ?? 0) < settings.minMeals ? (
          `Add ${settings.minMeals - (cart.itemCount ?? 0)} Meal(s) to Continue`
        ): (cart.itemCount ?? 0) > settings.maxMeals ? (
          `Remove ${(cart.itemCount ?? 0) - settings.maxMeals} Meal(s) to Continue`
        ): !settings.isReturn ? (
          'Proceed to Checkout'
        ): (
          'Place Order'
        )}
      </button>
      {settings.incompatSubscription && (
        <div className="SidecartSummary__Attention">
          <Attention width={29} />
          <span><a href={window.ThemeSettings.linkContactUs} target="_blank">Contact Us</a> if you are interested in switching to the Chef's Choice plan</span>
        </div>
      )}
      {!settings.isReturn && (
        <>
        <div className="SidecartSummary__Message">No need to be home. Our packaging will keep your meals fresh and cold for 12 hours after delivery.</div>
        </>
      )}
    </div>
  );
}

export default Summary;
